import React from 'react'
import { Link } from 'react-router-dom'
import Icon from '../../shared/icon'
import { useCatalogue } from '../CatalogueProvider'
import { NAME_COLUMN, nameColumnSpan, selectedColumns } from '../catalogueSelectors'
import { CatalogueItem, NamedRecord } from '../catalogueTypes'

const isNamedRecord = (value: unknown): value is NamedRecord =>
  typeof value === 'object' && value !== null && 'name' in value

const cellValue = (item: CatalogueItem, columnName: string): React.ReactNode => {
  const raw: unknown = (item as unknown as Record<string, unknown>)[columnName]
  if (Array.isArray(raw)) return raw.filter(isNamedRecord).map((record) => record.name).join(', ')
  if (isNamedRecord(raw)) return raw.name
  if (typeof raw === 'string' || typeof raw === 'number') return raw
  return null
}

interface CatalogueItemImageProps {
  item: CatalogueItem
  placeholder: string
  className?: string
}

export const CatalogueItemImage = ({ item, placeholder, className }: CatalogueItemImageProps) =>
  item.file_url ? <img className={className} src={item.file_url} /> : <Icon name={placeholder} className={className} />

const CatalogueItemNameCell = ({ item }: { item: CatalogueItem }) => {
  const { meta } = useCatalogue()
  const isCategory = item.class_name === 'ProductCategory'

  return (
    <div className="name-column">
      <CatalogueItemImage item={item} placeholder={isCategory ? 'categoryPlaceholder' : 'productPlaceholder'} className="table-row-image" />
      <Link
        className={isCategory ? 'table-row-name' : 'as-link pointer table-row-name'}
        to={isCategory ? meta.paths.category(item.id) : meta.paths.show(item)}
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        {item.name}
      </Link>
    </div>
  )
}

export const CatalogueItemCells = ({ item }: { item: CatalogueItem }) => {
  const { state } = useCatalogue()
  const { columns, editMode } = state
  const nameSpan = nameColumnSpan(columns)

  return (
    <>
      {selectedColumns(columns).map((column, index) => {
        const isName = column.column_name === NAME_COLUMN
        let value: React.ReactNode = isName ? <CatalogueItemNameCell item={item} /> : cellValue(item, column.column_name)
        if (editMode && index === 0) {
          value = (
            <div className="d-flex align-items-center">
              <Icon name="dragBurger" className="drag-icon me-2" />
              {value}
            </div>
          )
        }
        return (
          <td
            colSpan={isName ? nameSpan : 1}
            className={`td-${column.column_name} product-cell`}
            key={`${item.class_name}-${item.id}-${column.column_name}`}
          >
            {value}
          </td>
        )
      })}
    </>
  )
}

export const CatalogueRowActions = ({ children }: { children: React.ReactNode }) => <div className="icon-container">{children}</div>

export const CatalogueEditActions = ({ item }: { item: CatalogueItem }) => {
  const { state, actions, meta } = useCatalogue()
  if (!state.editMode) return null
  if (item.class_name === 'ProductCategory' && !meta.canManageCategories) return null

  return (
    <>
      <Icon
        className="icon-button"
        name="deleteIcon"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation()
          actions.requestDelete(item)
        }}
      />
      <Icon
        className="icon-button"
        name="editIcon"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation()
          meta.go.edit(item)
        }}
      />
    </>
  )
}

export const ExpandCategoryAction = ({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) => (
  <Icon
    className="icon-button"
    name={expanded ? 'arrowUp' : 'arrowDown'}
    onClick={(event: React.MouseEvent) => {
      event.stopPropagation()
      onToggle()
    }}
  />
)
